import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
  createElement,
} from 'react';
import {
  useCreateUserGeneratedContentMutation,
  useUpdateUserGeneratedContentMutation,
  useUpdateUserGeneratedContentStatusMutation,
  useUserGeneratedContentQuery,
} from '../../common/queries/use-user-generated-content-query';
import { UserGeneratedContent, UserGeneratedContentResponse } from './types';

export const MAXIMUM_TITLE_LENGTH = 125;
export const MAXIMUM_SUMMARY_LENGTH = 250;

type UpsertUserGeneratedContentType = {
  onSuccess: (response?: UserGeneratedContentResponse) => void;
  onError: (error: Error) => void;
};
type UserGeneratedContentContextType = {
  userGeneratedContent: UserGeneratedContent;
  updateUserGeneratedContentFormState: (
    draft: Partial<UserGeneratedContent>
  ) => void;

  upsertUserGeneratedContent: (props: UpsertUserGeneratedContentType) => void;
  submitDraft: (props: UpsertUserGeneratedContentType) => void;

  isPersisting: boolean;
  isValid: boolean;
  hasRequiredInput: boolean;
  isTitleOverLimit: boolean;
  error: string | undefined;
  isLoading: boolean;
};

const defaultUserGeneratedContent: UserGeneratedContent = {
  title: '',
  body: '',
  topic_ids: [],
  audiences: [],
  status: 'active',
};

const UserGeneratedContentContext = createContext<
  UserGeneratedContentContextType | undefined
>(undefined);

type UserGeneratedContentProviderProps = {
  children: ReactNode;
  id?: number;
};

export function UserGeneratedContentProvider({
  children,
  id,
}: UserGeneratedContentProviderProps) {
  const isNewDraft = !id;
  const [isReady, setIsReady] = useState(isNewDraft);
  const [userGeneratedContent, setUserGeneratedContent] =
    useState<UserGeneratedContent>(defaultUserGeneratedContent);
  const updateUserGeneratedContentFormState = useCallback(
    (updates: Partial<UserGeneratedContent>) => {
      setUserGeneratedContent((prev) => ({ ...prev, ...updates }));
    },
    []
  );

  const { data: fetchedDraft, error: fetchError } =
    useUserGeneratedContentQuery(id);

  useEffect(() => {
    if (fetchedDraft && !isNewDraft) {
      setUserGeneratedContent((prev) => ({
        ...prev,
        id: fetchedDraft.data.attributes.id,
        title: fetchedDraft.data.attributes.title,
        summary: fetchedDraft.data.attributes.summary,
        body: fetchedDraft.data.attributes.body,
        status: fetchedDraft.data.attributes.status,
        audiences: fetchedDraft.data.attributes.audiences,
        // topic_ids are local-only state (not returned by API), so don't overwrite
      }));
      setIsReady(true);
    }
  }, [fetchedDraft, isNewDraft]);

  const onSaveSuccess = useCallback(
    (response: UserGeneratedContentResponse) => {
      updateUserGeneratedContentFormState({
        id: response.data.attributes.id,
        title: response.data.attributes.title,
        summary: response.data.attributes.summary,
        body: response.data.attributes.body,
        status: response.data.attributes.status,
        // topic_ids are local-only state (not returned by API), so don't overwrite
      });
    },
    [updateUserGeneratedContentFormState]
  );

  const onSaveError = useCallback((err) => {
    console.error('User generated content submission error', err);
  }, []);

  const { isWorking: isCreating, mutate: createMutation } =
    useCreateUserGeneratedContentMutation({
      onSuccess: onSaveSuccess,
      onError: onSaveError,
    });

  const { isWorking: isUpdating, mutate: updateMutateFn } =
    useUpdateUserGeneratedContentMutation(userGeneratedContent.id, {
      onSuccess: onSaveSuccess,
      onError: onSaveError,
    });

  const updateMutation = useCallback(
    (
      draft: UserGeneratedContent,
      options: {
        onSuccess: (response?: UserGeneratedContentResponse) => void;
        onError: (error: Error) => void;
      }
    ) => {
      if (draft.id) {
        updateMutateFn(draft, options);
      }
    },
    [updateMutateFn]
  );

  const upsertUserGeneratedContent = useCallback(
    ({ onSuccess, onError }: UpsertUserGeneratedContentType) => {
      if (userGeneratedContent.id) {
        updateMutation(userGeneratedContent, { onSuccess, onError });
      } else {
        createMutation(userGeneratedContent, { onSuccess, onError });
      }
    },
    [createMutation, updateMutation, userGeneratedContent]
  );

  const { isWorking: isSubmitting, mutate: statusMutation } =
    useUpdateUserGeneratedContentStatusMutation();

  const submittingRef = useRef(false);

  const submitDraft = useCallback(
    ({ onSuccess, onError }: UpsertUserGeneratedContentType) => {
      if (submittingRef.current) return;
      submittingRef.current = true;

      upsertUserGeneratedContent({
        onSuccess: (response?: UserGeneratedContentResponse) => {
          const contentId =
            response?.data?.attributes?.id ?? userGeneratedContent.id;
          if (!contentId) {
            submittingRef.current = false;
            onError(new Error('Content ID not available after save'));
            return;
          }
          statusMutation(
            { id: contentId, status: 'submitted' },
            {
              onSuccess: () => {
                submittingRef.current = false;
                onSuccess();
              },
              onError: (err) => {
                submittingRef.current = false;
                onError(err);
              },
            }
          );
        },
        onError: (err) => {
          submittingRef.current = false;
          onError(err);
        },
      });
    },
    [upsertUserGeneratedContent, statusMutation, userGeneratedContent.id]
  );

  const hasRequiredInput = useMemo(
    () =>
      !!(
        userGeneratedContent.title &&
        userGeneratedContent.body &&
        userGeneratedContent.topic_ids?.length
      ),
    [
      userGeneratedContent.title,
      userGeneratedContent.body,
      userGeneratedContent.topic_ids,
    ]
  );

  const isTitleOverLimit = useMemo(() => {
    if (!userGeneratedContent.title) {
      return false;
    }
    return userGeneratedContent.title.length > MAXIMUM_TITLE_LENGTH;
  }, [userGeneratedContent.title]);

  const isSummaryOverLimit = useMemo(() => {
    if (!userGeneratedContent.summary) {
      return false;
    }
    return userGeneratedContent.summary.length > MAXIMUM_SUMMARY_LENGTH;
  }, [userGeneratedContent.summary]);

  const isValid = useMemo(() => {
    const inputIsValid = !isTitleOverLimit && !isSummaryOverLimit;
    return hasRequiredInput && inputIsValid;
  }, [isTitleOverLimit, isSummaryOverLimit, hasRequiredInput]);

  const value = {
    userGeneratedContent,
    updateUserGeneratedContentFormState,
    isPersisting: isCreating || isUpdating || isSubmitting,
    isValid,
    hasRequiredInput,
    isTitleOverLimit,
    error: fetchError?.message,
    upsertUserGeneratedContent,
    submitDraft,
    isLoading: !isReady,
  };

  return createElement(
    UserGeneratedContentContext.Provider,
    { value },
    children
  );
}

export function useUserGeneratedContent(): UserGeneratedContentContextType {
  const context = useContext(UserGeneratedContentContext);
  if (context === undefined) {
    throw new Error(
      'useUserGeneratedContent must be used within a UserGeneratedContentProvider'
    );
  }
  return context;
}

export { UserGeneratedContentContext };
